import { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { createLocation } from '../../api';

interface CreateLocationPopoverProps {
  currentSite: string | null;
  currentOrg: string | null;
  onCreated: (location: { _id: string; name: string }) => void;
}

export function CreateLocationPopover({
  currentSite,
  currentOrg,
  onCreated,
}: CreateLocationPopoverProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    setLoading(true);
    try {
      const location = await createLocation({
        name: trimmed,
        site: currentSite ?? undefined,
        organisation: currentOrg ?? undefined,
      });
      toast.success(`Location "${location.name}" created`);
      onCreated(location);
      setName('');
      setOpen(false);
    } catch {
      toast.error('Failed to create location');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0">
          <Plus className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <div className="space-y-3">
          <h4 className="font-medium text-sm">Create Location</h4>
          <div className="space-y-2">
            <Label htmlFor="new-location-name">Name</Label>
            <Input
              id="new-location-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Location name"
              autoFocus
            />
          </div>
          <Button
            size="sm"
            className="w-full"
            onClick={handleSubmit}
            disabled={!name.trim() || loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
